import { Box, Chip, LinearProgress, Typography, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import useStyles from "../styles";
import { DataGrid, GridRenderCellParams } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { alpha, darken, lighten, Stack } from "@mui/system";
import dayjs from "dayjs";
import handleColors from "../utils/handleColors";
import {
  CustomGridColDefCustomText,
  CustomGridColDefOthers,
  MyDataGridConfig,
} from "./types";

const MyDataGrid: React.FC<MyDataGridConfig> = (props) => {
  const { data, columns } = props;
  const theme = useTheme();
  const styles = useStyles(theme);
  const darkMode = theme.palette.mode === "dark";
  const dataGridRef = useRef<HTMLDivElement | null>(null);
  const [rowHeight, setRowHeight] = useState<number>(70);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const stylesRenderCells = {
    containerColumnLastLogin: {
      display: "flex",
      justifyContent: "center",
      flexDirection: { xs: "row", md: "column" },
      alignItems: { xs: "center", md: "flex-start" },
      height: "100%",
    },
    textDate: {
      //color: "text.primary",
      fontWeight: 500,
    },
    subTextDate: {
      color: "text.primary",
      fontWeight: 400,
    },
    containerColumn: {
      display: "flex",
      flexWrap: "wrap",
      height: "100%",
      width: "100%",
      fontWeight: "bold",
      justifyContent: { xs: "end", md: "center" },
      alignItems: "center",
      flexDirection: "row",
      gap: 0.5,
      padding: { xs: 0, md: 1 },
    },
  };

  function addIdToObject<T extends object>(arr: T[]): (T & { id: number })[] {
    return arr.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  }

  const dataWithId = addIdToObject(data ?? []);

  const renderCellData = (params: GridRenderCellParams) => {
    return params.value ? (
      <Stack sx={stylesRenderCells.containerColumnLastLogin}>
        <Typography fontSize={13} sx={stylesRenderCells.textDate}>
          {dayjs(params.value).locale("pt-br").format("D [de] MMM, YYYY")}
        </Typography>
        <Typography fontSize={12} sx={stylesRenderCells.subTextDate}>
          {dayjs(params.value).locale("pt-br").format("[às] HH:mm:ss")}
        </Typography>
      </Stack>
    ) : (
      <>-</>
    );
  };

  const renderCellProgress = (params: GridRenderCellParams, data: any[]) => {
    const colorsByKey = (params.colDef as unknown as CustomGridColDefOthers)
      .colorsByKey;

    const orderedData = data
      .slice()
      .sort((a, b) => Number(b?.[params.field]) - Number(a?.[params.field]));

    const maxValue = Number(orderedData[0][params.field]);
    const value = params.value ? Number(params.value) : 0;
    const progress = maxValue ? (value / maxValue) * 100 : 0;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          padding: 0.5,
        }}
      >
        <LinearProgress
          variant="buffer"
          value={progress}
          valueBuffer={100}
          sx={{
            width: "80%",
            mr: 1,
            height: 8,
            borderRadius: 2,
            "& .MuiLinearProgress-bar1Buffer": {
              backgroundColor:
                colorsByKey && Object.keys(colorsByKey).length > 0
                  ? handleColors(params.value.toString(), theme, colorsByKey)
                  : theme.palette.primary.main,
            },
          }}
        />
        <Typography
          sx={{
            fontWeight: "bold",
            color:
              colorsByKey && Object.keys(colorsByKey).length > 0
                ? handleColors(params.value.toString(), theme, colorsByKey)
                : theme.palette.primary.main,
          }}
        >
          {value}
        </Typography>
      </Box>
    );
  };

  const renderCellChip = (params: GridRenderCellParams) => {
    const colorsByKey = (params.colDef as unknown as CustomGridColDefOthers)
      .colorsByKey;

    return (
      <>
        {params?.value ? (
          <Stack sx={stylesRenderCells.containerColumn}>
            {params?.value
              ?.toString()
              ?.split(",")
              ?.map((row: string, index: number) => (
                <Chip
                  key={index}
                  label={row}
                  sx={{
                    color: colorsByKey
                      ? darkMode
                        ? handleColors(row, theme, colorsByKey)
                        : darken(handleColors(row, theme, colorsByKey), 0.3)
                      : darkMode
                        ? lighten(theme.palette.secondary.main, 0.3)
                        : darken(theme.palette.secondary.main, 0.3),
                    backgroundColor: colorsByKey
                      ? alpha(handleColors(row, theme, colorsByKey), 0.1)
                      : alpha(theme.palette.secondary.main, 0.2),
                  }}
                />
              ))}
          </Stack>
        ) : (
          " "
        )}
      </>
    );
  };

  const renderCellText = (params: GridRenderCellParams) => {
    const colorsByKey = (params.colDef as unknown as CustomGridColDefCustomText)
      .colorsByKey;

    return (
      <Typography
        fontSize={13}
        sx={stylesRenderCells.textDate}
        color={
          colorsByKey && typeof params?.value === "number"
            ? darkMode
              ? lighten(
                  handleColors("color", theme, colorsByKey[params?.value]),
                  0.2,
                )
              : darken(
                  handleColors("color", theme, colorsByKey[params?.value]),
                  0.3,
                )
            : darkMode
              ? lighten(theme.palette.primary.main, 0.2)
              : darken(theme.palette.primary.main, 0.3)
        }
      >
        {colorsByKey && typeof params?.value === "number"
          ? colorsByKey[params.value]?.text
          : params?.value}
      </Typography>
    );
  };
  const customColumns = columns?.map((column) => {
    if (column.myColumnType === "data") {
      return {
        ...column,
        renderCell: (params: GridRenderCellParams) => renderCellData(params),
      };
    }
    if (column.myColumnType === "progress") {
      return {
        ...column,
        renderCell: (params: GridRenderCellParams) =>
          renderCellProgress(params, data),
      };
    }
    if (column.myColumnType === "chip") {
      return {
        ...column,
        renderCell: (params: GridRenderCellParams) => renderCellChip(params),
      };
    }
    if (column.myColumnType === "customText") {
      return {
        ...column,
        renderCell: (params: GridRenderCellParams) => renderCellText(params),
      };
    }
    if (column.myColumnType === "money") {
      return {
        ...column,
        valueFormatter: (value?: number) => {
          if (value == null) return "";
          return value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          });
        },
      };
    }
    return column;
  });

  useEffect(() => {
    if (dataGridRef.current) {
      let { offsetHeight } = dataGridRef.current;
      offsetHeight = offsetHeight - 45 - 50;
      let newRowHeight = 45;
      const newPageSize = Math.floor(offsetHeight / newRowHeight);
      newRowHeight = Math.max(45, offsetHeight / newPageSize);

      setRowHeight(newRowHeight);
      setPaginationModel({
        pageSize: newPageSize,
        page: 0,
      });
    }
  }, [dataGridRef.current]);

  return (
    <DataGrid
      ref={dataGridRef}
      paginationModel={paginationModel}
      //autoPageSize
      onPaginationModelChange={setPaginationModel}
      rows={dataWithId}
      columns={customColumns ?? []}
      density="standard"
      disableColumnMenu
      disableColumnFilter
      hideFooterSelectedRowCount
      localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
      sx={styles.dataGrid}
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row"
      }
      rowHeight={rowHeight}
      //pageSizeOptions={[5, 10, 20]}
      initialState={{
        //  ...rows.initialState,
        pagination: {
          paginationModel: { pageSize: 5 },
        },
      }}
      className="cancelSelectorName"
    />
  );
};

export default MyDataGrid;
